import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shortlistService } from '../services/shortlist';
import { useShortlist } from '../context/ShortlistContext';
import '../styles/Shortlisted.css';

const getImageSrc = (images_urls) => {
  if (!images_urls || images_urls.length === 0) return null;
  const url = images_urls[0];
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `http://localhost:3001${url}`;
};

const Shortlisted = () => {
  const [shortlistedCars, setShortlistedCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const { refreshShortlist } = useShortlist();

  useEffect(() => { loadShortlistedCars(); }, [currentPage]);

  const loadShortlistedCars = async () => {
    try {
      setLoading(true);
      const response = await shortlistService.getShortlistedCars(currentPage);
      if (response.success) {
        setShortlistedCars(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading shortlisted cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (carId) => {
    try {
      setRemovingId(carId);
      await shortlistService.removeFromShortlist(carId);
      await refreshShortlist();
      setShortlistedCars(prev => prev.filter(item => item.car.id !== carId));
      if (pagination) setPagination(prev => ({ ...prev, total_count: Math.max(0, prev.total_count - 1) }));
    } catch (error) {
      console.error('Error removing car:', error);
    } finally {
      setRemovingId(null);
    }
  };

  // Calculate simple EMI (approx 8.5% for 5 years)
  const calcEMI = (price) => {
    const p = parseFloat(price);
    if (!p) return null;
    const r = 8.5 / 12 / 100;
    const n = 60;
    const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi).toLocaleString('en-IN');
  };

  if (loading) {
    return (
      <div className="sl-page">
        <div className="sl-loading">
          <div className="sl-spinner"></div>
          <p>Loading your shortlisted cars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sl-page">
      <div className="sl-container">
        {/* Header */}
        <div className="sl-header">
          <h1 className="sl-title">
            <span className="sl-count-pill">{pagination?.total_count || shortlistedCars.length}</span>
            Shortlisted Cars
          </h1>
        </div>

        {shortlistedCars.length === 0 ? (
          <div className="sl-empty">
            <div className="sl-empty-icon">❤️</div>
            <h2>No Shortlisted Cars</h2>
            <p>Start browsing and save cars you love!</p>
            <Link to="/buy-cars" className="sl-browse-btn">Browse Cars</Link>
          </div>
        ) : (
          <>
            <div className="sl-list">
              {shortlistedCars.map((item) => {
                const car = item.car;
                const imgSrc = getImageSrc(car.images_urls);
                const emi = calcEMI(car.price);

                return (
                  <div key={item.id} className="sl-card">
                    {/* ── Image ── */}
                    <Link to={`/car/${car.id}`} className="sl-card-img-wrap">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={`${car.brand} ${car.model}`}
                          className="sl-card-img"
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="sl-card-no-img"
                        style={{ display: imgSrc ? 'none' : 'flex' }}
                      >
                        🚗
                      </div>
                    </Link>

                    {/* ── Details ── */}
                    <div className="sl-card-body">
                      {/* Top: name + heart */}
                      <div className="sl-card-top">
                        <Link to={`/car/${car.id}`} className="sl-card-name">
                          {car.brand} {car.model}
                          {car.year && <span className="sl-card-year"> {car.year}</span>}
                        </Link>
                        <button
                          className={`sl-heart-btn${removingId === car.id ? ' removing' : ''}`}
                          onClick={() => handleRemove(car.id)}
                          disabled={removingId === car.id}
                          title="Remove from shortlist"
                        >
                          ❤️
                        </button>
                      </div>

                      {/* Variant */}
                      {car.variant && <p className="sl-card-variant">{car.variant}</p>}

                      {/* Specs */}
                      <div className="sl-card-specs">
                        {car.km_driven && (
                          <span className="sl-spec">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                            {Number(car.km_driven).toLocaleString('en-IN')} km
                          </span>
                        )}
                        {car.fuel_type && (
                          <span className="sl-spec">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 22V8l9-6 9 6v14"/></svg>
                            {car.fuel_type}
                          </span>
                        )}
                        {car.transmission && (
                          <span className="sl-spec">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="5" cy="12" r="3"/><circle cx="19" cy="5" r="3"/><circle cx="19" cy="19" r="3"/><path d="M5 15v1a6 6 0 006 6h1M5 9V8a6 6 0 016-6h1"/></svg>
                            {car.transmission}
                          </span>
                        )}
                        {car.location && (
                          <span className="sl-spec">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            {car.location}
                          </span>
                        )}
                      </div>

                      {/* Badges */}
                      {(car.certified || car.condition) && (
                        <div className="sl-card-badges">
                          {car.certified && <span className="sl-badge sl-badge-assured">✓ Assured</span>}
                          {car.condition && <span className="sl-badge sl-badge-condition">{car.condition}</span>}
                        </div>
                      )}

                      {/* Price + CTA */}
                      <div className="sl-card-price-row">
                        <div className="sl-price-block">
                          <span className="sl-card-price">
                            ₹{parseFloat(car.price).toLocaleString('en-IN')}
                          </span>
                          {emi && (
                            <span className="sl-card-emi">EMI ₹{emi}/m*</span>
                          )}
                        </div>
                        <Link to={`/car/${car.id}`} className="sl-view-btn">
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination?.total_pages > 1 && (
              <div className="sl-pagination">
                <button className="sl-pg-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Previous</button>
                <span className="sl-pg-info">Page {currentPage} of {pagination.total_pages}</span>
                <button className="sl-pg-btn" onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))} disabled={currentPage === pagination.total_pages}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Shortlisted;